/**
 * @file: useMyPerformance.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The member's own work over the last 30 days, shown on the Me home screen.
 * The server decides which parts the member gets from their roles.
 */

import { MemberPerformance } from '@euler/model/MemberPerformance';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';

export type MyPerformanceState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'ready'; performance: MemberPerformance };

export const useMyPerformance = () => {
  const { userService } = useServiceFactory();
  const [state, setState] = useState<MyPerformanceState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    userService
      .getMyPerformance()
      .then(performance => {
        if (active) setState({ kind: 'ready', performance });
      })
      .catch(error => {
        console.error('Loading my performance failed', error);
        // A reload that fails keeps the figures already on screen.
        if (active) {
          setState(prev => (prev.kind === 'ready' ? prev : { kind: 'failed' }));
        }
      });
    return () => {
      active = false;
    };
  }, [userService, attempt]);

  const reload = useCallback(() => {
    setState(prev => (prev.kind === 'failed' ? { kind: 'loading' } : prev));
    setAttempt(v => v + 1);
  }, []);

  return { state, reload };
};
